import React, { useEffect, useRef, useState } from "react";
import { SendIcon } from "./Icons";
import { isMobile } from "./utils";
import {
  FLOW_MAP, DEST_GROUPS, ALL_DESTS, PICKUP_POINTS, AI_PLACEHOLDER, AI_SUGGESTIONS,
} from "./flowConfig";

const PILL =
  "whitespace-nowrap px-3 py-1.5 bg-slate-100 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 " +
  "text-slate-600 hover:text-indigo-700 rounded-full text-xs font-medium transition-colors disabled:opacity-50";
const INPUT =
  "w-full bg-slate-50 border border-slate-200 text-slate-800 text-base sm:text-sm rounded-xl pl-4 pr-12 py-3.5 " +
  "focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all shadow-inner disabled:opacity-60";
const SEND =
  "absolute right-2 p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 " +
  "disabled:hover:bg-indigo-600 transition-colors flex items-center justify-center shadow-sm";

function TextRow({ type = "text", placeholder, min, disabled, onSubmit, inputRef, value, onChange, maxLength }) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="relative flex items-center"
    >
      <input
        ref={inputRef} type={type} value={value} min={min} maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)} placeholder={placeholder} disabled={disabled}
        className={INPUT} aria-label={placeholder || "Message"}
      />
      <button type="submit" disabled={disabled || !value.trim()} className={SEND} aria-label="Send message">
        <SendIcon />
      </button>
    </form>
  );
}

// One question of the scripted enquiry flow
function StepControls({ step, answers, busy, onSubmit }) {
  const [text, setText] = useState("");
  const type = step.type;

  let pills = [];
  if (type === "buttons" || type === "buttons-with-input") pills = step.options;
  else if (type === "input-with-suggestions") {
    const q = text.trim().toLowerCase();
    pills = q
      ? ALL_DESTS.filter((d) => d.toLowerCase().includes(q))
      : DEST_GROUPS[answers.tripType] || ALL_DESTS.slice(0, 8);
    pills = pills.slice(0, 10);
  } else if (type === "pickup" || type === "drop") pills = PICKUP_POINTS[answers[step.citykey]] || [];

  const showInput = type !== "buttons";
  const today = new Date().toISOString().split("T")[0];
  const min = type === "date" ? (step.id === "hotelCheckOut" && answers.hotelCheckIn) || today : undefined;
  const placeholder =
    step.placeholder ||
    (type === "input-with-suggestions" ? "Type destination..." :
     type === "pickup" || type === "drop" ? "Type pickup / drop point..." : "Type here...");

  return (
    <div className="space-y-3">
      {(pills.length > 0 || step.optional) && (
        <div className="flex flex-wrap gap-2 max-h-[36vh] overflow-y-auto">
          {pills.map((p) => (
            <button key={p} type="button" disabled={busy} onClick={() => onSubmit(p)} className={PILL}>{p}</button>
          ))}
          {step.optional && (
            <button type="button" disabled={busy} onClick={() => onSubmit("Skipped")} className={PILL}>Skip</button>
          )}
        </div>
      )}
      {showInput && (
        <TextRow
          type={type === "date" ? "date" : "text"} min={min} placeholder={placeholder}
          value={text} onChange={setText} disabled={busy}
          onSubmit={() => text.trim() && onSubmit(text)}
        />
      )}
    </div>
  );
}

function AiControls({ busy, onSend, showChips }) {
  const [text, setText] = useState("");
  const ref = useRef(null);

  // put the cursor back after each reply (desktop only, so mobile keyboards don't pop up)
  useEffect(() => {
    if (!busy && !isMobile()) ref.current?.focus();
  }, [busy]);

  const send = () => {
    if (!text.trim()) return;
    onSend(text);
    setText("");
  };

  return (
    <div className="space-y-3">
      <TextRow
        inputRef={ref} placeholder={AI_PLACEHOLDER} value={text} onChange={setText}
        disabled={busy} maxLength={1000} onSubmit={send}
      />
      {showChips && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {AI_SUGGESTIONS.map((s) => (
            <button key={s} type="button" disabled={busy} onClick={() => onSend(s.replace(/^[^\w\s]+\s*/u, ""))} className={PILL}>{s}</button>
          ))}
        </div>
      )}
      <p className="text-[10px] text-slate-400 text-center m-0">
        Real-time Himalayan itineraries &amp; rates. Click any tour, cab or stay to preview details.
      </p>
    </div>
  );
}

export default function Controls({ chat }) {
  const { stepId, aiMode, busy, answers, messages } = chat;
  const step = stepId ? FLOW_MAP[stepId] : null;

  if (aiMode) {
    return <AiControls busy={busy} onSend={chat.sendAi} showChips />;
  }

  if (step) {
    return <StepControls key={step.id} step={step} answers={answers} busy={busy} onSubmit={chat.submitAnswer} />;
  }

  // Nothing to answer (chat ended / interrupted) -> offer a fresh start
  if (!busy && messages.length > 0) {
    return (
      <div className="flex gap-2">
        <button type="button" onClick={chat.resetChat} className={PILL}>Start a new chat</button>
      </div>
    );
  }
  return null;
}